import type { Language } from "./language";

interface ToolText {
  title: string;
  summary: string;
}

/**
 * Each tool's title and one-line summary per language, keyed by the tool's
 * name. The backend's catalogue holds the English text Claude sees; a tool
 * missing here (a new one) falls back to it. The Dutch list names every tool
 * DC Data serves or announces, whatever the reader's role (`isKnownTool`):
 * drop a tool's entries when DC Data stops serving it, so its earlier calls
 * read as no longer available.
 */
const TOOL_TEXT: Record<Language, Record<string, ToolText>> = {
  nl: {
    whoami: {
      title: "Wie ben ik",
      summary: "Het De Ceuster-account waarmee je aangemeld bent",
    },
    describe_model: {
      title: "Odoo-model beschrijven",
      summary:
        "Wat de velden en codes in Odoo betekenen, zoals een verkooptype of de status van een kraan",
    },
    search_clients: {
      title: "Klanten zoeken",
      summary: "Klanten op naam, btw-nummer, gemeente of label",
    },
    get_client: {
      title: "Klantgegevens",
      summary:
        "Eén klant volledig: adressen, werven, verkoper; openstaande facturen met Financieel",
    },
    list_cranes: {
      title: "Kranen oplijsten",
      summary: "De kranen met hun type, capaciteit en of ze vrij of verhuurd zijn",
    },
    search_orders: {
      title: "Orders en verhuringen zoeken",
      summary: "Orders en huurcontracten per klant, kraan, periode of type",
    },
    search_invoices: {
      title: "Facturen zoeken",
      summary:
        "Facturen en creditnota's per klant, datum of kraan, met wat nog openstaat of vervallen is",
    },
    search_payments: {
      title: "Betalingen zoeken",
      summary: "Ontvangen betalingen per klant en periode, met de facturen die ze voldeden",
    },
    search_products: {
      title: "Producten zoeken",
      summary:
        "Producten op code, naam of categorie, met verkoopprijs en leveranciers; kostprijs en marge met Financieel",
    },
    order_locations: {
      title: "Locaties van orders (Inzetkaart)",
      summary: "Waar de orders waren: elke werf op de kaart, met de afstand vanaf Geel",
    },
    revenue_summary: {
      title: "Omzetoverzicht",
      summary: "Gefactureerde omzet per klant, werf, kraan, activiteit, product of maand",
    },
    // The occupancy tools, computed from Odoo at the call with the app Bezettingsgraad's
    // rules: Kranen tools, showing client names only to whoever may read them.
    occupancy_alerts: {
      title: "Meldingen van de bezettingsgraad",
      summary:
        "De openstaande meldingen van Bezettingsgraad, zoals een kraantype zonder vrije kraan",
    },
    crane_occupancy: {
      title: "Bezettingsgraad per periode",
      summary:
        "De bezettingsgraad per dag, week of maand sinds 2024 en tot een jaar vooruit volgens de boekingen, voor de vloot, BK, TK of per kraantype",
    },
    crane_availability: {
      title: "Kraanbeschikbaarheid",
      summary: "Welke kranen vrij zijn in een periode, met de verhuringen die al geboekt zijn",
    },
    // The finance checks, computed from Odoo at the call.
    unbilled_work: {
      title: "Niet-gefactureerd werk",
      summary: "Geleverd werk dat niet of maar deels gefactureerd is, met de eigen vloot apart",
    },
    transport_price_check: {
      title: "Transportprijscheck",
      summary: "Gefactureerde transportprijzen tegenover het tarief en de afstand tot de werf",
    },
    // Coming soon: listed on the Tools page, not served yet.
    crane_contracts: {
      title: "Kraancontracten",
      summary: "Contracten per kraan, zoals verzekering en leasing, met hun einddatum",
    },
    add_client_note: {
      title: "Notitie bij een klant",
      summary: "Een notitie toevoegen aan de historiek van een klant in Odoo",
    },
    draft_quote: {
      title: "Ontwerpofferte",
      summary:
        "Een ontwerpofferte voor een klant en een kraan, om na te kijken en te versturen vanuit Odoo",
    },
    schedule_activity: {
      title: "Opvolging plannen",
      summary: "Een telefoontje of een taak inplannen voor een collega, bij een klant of een order",
    },
    client_overview: {
      title: "Klantoverzicht",
      summary:
        "Eén klant in één oogopslag: verhuringen, gebruikte kranen, omzetverloop en laatste order",
    },
    client_payment_profile: {
      title: "Betaalgedrag",
      summary:
        "Hoe een klant betaalt: gebruikelijke betaaltermijn, aandeel te laat betaald en wat nu openstaat",
    },
    connector_usage: {
      title: "Gebruik van de connector",
      summary: "Oproepen per collega, tool en dag, met fouten",
    },
    connector_users: {
      title: "Gebruikers van de connector",
      summary: "Wie de connector gebruikt, sinds wanneer, en wie geblokkeerd is",
    },
    // The submissions and the tools of the dashboard's roles (the roadmap, its triage and
    // the connector's issues), which the overview lists to whoever gets them.
    report_gap: {
      title: "Onbeantwoorde vraag doorgeven",
      summary: "Een vraag die DC Data niet kon beantwoorden doorgeven aan Velta, voor de roadmap",
    },
    submit_idea: {
      title: "Idee doorgeven aan Velta",
      summary: "Een idee of een vraag voor DC Data doorgeven aan Velta, voor de roadmap",
    },
    my_submissions: {
      title: "Mijn ideeën en vragen",
      summary: "Wat er werd van de ideeën en vragen die je aan Velta doorgaf",
    },
    roadmap_month: {
      title: "Maand van de roadmap",
      summary: "Eén maand van de roadmap met zijn ideeën, zoals de pagina Roadmap ze je toont",
    },
    roadmap_item: {
      title: "Roadmap-item",
      summary: "Roadmap-items zoeken, of één item lezen met zijn opmerkingen, blokkades en volgers",
    },
    update_roadmap_item: {
      title: "Roadmap-item wijzigen",
      summary:
        "Een roadmap-item wijzigen, inplannen, volgen of verwijderen, zoals de pagina Roadmap het je toelaat",
    },
    roadmap_comment: {
      title: "Opmerking bij een roadmap-item",
      summary: "Een opmerking plaatsen bij een roadmap-item, of er een verwijderen",
    },
    roadmap_blocker: {
      title: "Blokkade van een roadmap-item",
      summary: "Toevoegen, oplossen of weghalen waarop een roadmap-item wacht",
    },
    set_roadmap_budget: {
      title: "Budget van de roadmap instellen",
      summary: "Het budget van één maand van de roadmap, in uren",
    },
    create_roadmap_item: {
      title: "Roadmap-item toevoegen",
      summary: "Een nieuw idee rechtstreeks op de roadmap, met zijn Linear-tickets",
    },
    list_triage: {
      title: "Triage van de roadmap",
      summary: "De ideeën en onbeantwoorde vragen die wachten op een ontwikkelaar",
    },
    triage_entry: {
      title: "Triage beoordelen",
      summary: "Een idee of vraag uit Triage op de roadmap zetten, of sluiten",
    },
    tool_issues: {
      title: "Problemen bekijken",
      summary:
        "De mislukte oproepen gebundeld in problemen, of één probleem met zijn oproepen en geschiedenis",
    },
    update_tool_issue: {
      title: "Probleem behandelen",
      summary:
        "Een probleem in behandeling nemen, oplossen, negeren of heropenen, of er een notitie bij zetten",
    },
  },
  // English comes from the backend's catalogue, the text Claude itself sees.
  en: {},
  fr: {
    whoami: {
      title: "Qui suis-je",
      summary: "Le compte De Ceuster avec lequel vous êtes connecté",
    },
    describe_model: {
      title: "Décrire un modèle Odoo",
      summary:
        "La signification des champs et des codes dans Odoo, comme un type de vente ou le statut d'une grue",
    },
    search_clients: {
      title: "Rechercher des clients",
      summary: "Des clients par nom, numéro de TVA, localité ou étiquette",
    },
    get_client: {
      title: "Détails du client",
      summary:
        "Un client en détail\u00a0: adresses, chantiers, commercial\u00a0; factures ouvertes avec Finances",
    },
    list_cranes: {
      title: "Liste des grues",
      summary: "Les grues avec leur type, leur capacité et si elles sont libres ou en location",
    },
    search_orders: {
      title: "Rechercher des commandes et des locations",
      summary: "Commandes et contrats de location par client, grue, période ou type",
    },
    search_invoices: {
      title: "Rechercher des factures",
      summary:
        "Factures et notes de crédit par client, date ou grue, avec ce qui reste ouvert ou est échu",
    },
    search_payments: {
      title: "Rechercher des paiements",
      summary:
        "Les paiements reçus par client et par période, avec les factures qu'ils ont réglées",
    },
    search_products: {
      title: "Rechercher des produits",
      summary:
        "Produits par code, nom ou catégorie, avec prix de vente et fournisseurs\u00a0; prix de revient et marge avec Finances",
    },
    order_locations: {
      title: "Emplacements des commandes (Inzetkaart)",
      summary:
        "Où se trouvaient les commandes\u00a0: chaque chantier sur la carte, avec sa distance depuis Geel",
    },
    revenue_summary: {
      title: "Synthèse du chiffre d'affaires",
      summary: "Chiffre d'affaires facturé par client, chantier, grue, activité, produit ou mois",
    },
    // The occupancy tools, computed from Odoo at the call with the app Taux d'occupation's
    // rules: Grues tools, showing client names only to whoever may read them.
    occupancy_alerts: {
      title: "Alertes du taux d'occupation",
      summary:
        "Les alertes ouvertes de l'application Taux d'occupation, comme un type de grue sans grue libre",
    },
    crane_occupancy: {
      title: "Taux d'occupation par période",
      summary:
        "Le taux d'occupation par jour, semaine ou mois depuis 2024 et jusqu'à un an à venir selon les réservations, pour la flotte, BK, TK ou par type de grue",
    },
    crane_availability: {
      title: "Disponibilité des grues",
      summary: "Les grues libres sur une période, avec les locations déjà réservées",
    },
    // The finance checks, computed from Odoo at the call.
    unbilled_work: {
      title: "Travaux non facturés",
      summary:
        "Les travaux livrés mais pas ou seulement en partie facturés, la propre flotte à part",
    },
    transport_price_check: {
      title: "Contrôle des prix de transport",
      summary:
        "Les prix de transport facturés comparés au tarif et à la distance jusqu'au chantier",
    },
    // Coming soon: listed on the Tools page, not served yet.
    crane_contracts: {
      title: "Contrats des grues",
      summary: "Les contrats par grue, comme l'assurance et le leasing, avec leur date de fin",
    },
    add_client_note: {
      title: "Note sur un client",
      summary: "Ajouter une note à l'historique d'un client dans Odoo",
    },
    draft_quote: {
      title: "Projet de devis",
      summary: "Un projet de devis pour un client et une grue, à vérifier et à envoyer depuis Odoo",
    },
    schedule_activity: {
      title: "Planifier un suivi",
      summary: "Planifier un appel ou une tâche pour un collègue, sur un client ou une commande",
    },
    client_overview: {
      title: "Aperçu du client",
      summary:
        "Un client en un coup d'œil\u00a0: locations, grues utilisées, évolution du chiffre d'affaires et dernière commande",
    },
    client_payment_profile: {
      title: "Comportement de paiement",
      summary:
        "La façon dont un client paie\u00a0: délai de paiement habituel, part des paiements en retard et ce qui reste ouvert",
    },
    connector_usage: {
      title: "Utilisation du connecteur",
      summary: "Les appels par collègue, outil et jour, avec les erreurs",
    },
    connector_users: {
      title: "Utilisateurs du connecteur",
      summary: "Qui utilise le connecteur, depuis quand, et qui est bloqué",
    },
    // The submissions and the tools of the dashboard's roles (the roadmap, its triage and
    // the connector's issues), which the overview lists to whoever gets them.
    report_gap: {
      title: "Signaler une question sans réponse",
      summary:
        "Transmettre à Velta une question à laquelle DC Data n'a pas pu répondre, pour la feuille de route",
    },
    submit_idea: {
      title: "Transmettre une idée à Velta",
      summary: "Transmettre à Velta une idée ou une demande pour DC Data, pour la feuille de route",
    },
    my_submissions: {
      title: "Mes idées et questions",
      summary: "Ce que sont devenues les idées et questions que vous avez transmises à Velta",
    },
    roadmap_month: {
      title: "Mois de la feuille de route",
      summary:
        "Un mois de la feuille de route et ses idées, tel que la page Roadmap vous le montre",
    },
    roadmap_item: {
      title: "Élément de la feuille de route",
      summary:
        "Rechercher des éléments de la feuille de route, ou en lire un avec ses commentaires, ses blocages et les personnes qui le suivent",
    },
    update_roadmap_item: {
      title: "Modifier un élément de la feuille de route",
      summary:
        "Modifier, planifier, suivre ou supprimer un élément de la feuille de route, comme la page Roadmap vous le permet",
    },
    roadmap_comment: {
      title: "Commenter un élément de la feuille de route",
      summary: "Publier un commentaire sur un élément de la feuille de route, ou en supprimer un",
    },
    roadmap_blocker: {
      title: "Blocage d'un élément de la feuille de route",
      summary: "Ajouter, résoudre ou retirer ce qu'attend un élément de la feuille de route",
    },
    set_roadmap_budget: {
      title: "Fixer le budget de la feuille de route",
      summary: "Le budget d'un mois de la feuille de route, en heures",
    },
    create_roadmap_item: {
      title: "Ajouter un élément à la feuille de route",
      summary: "Une nouvelle idée directement sur la feuille de route, avec ses tickets Linear",
    },
    list_triage: {
      title: "Triage de la feuille de route",
      summary: "Les idées et les questions sans réponse qui attendent l'examen d'un développeur",
    },
    triage_entry: {
      title: "Examiner une entrée du triage",
      summary: "Mettre une idée ou une question du triage sur la feuille de route, ou la fermer",
    },
    tool_issues: {
      title: "Consulter les problèmes",
      summary:
        "Les appels échoués regroupés en problèmes, ou un problème avec ses appels et son historique",
    },
    update_tool_issue: {
      title: "Traiter un problème",
      summary: "Prendre en charge, résoudre, ignorer ou rouvrir un problème, ou y ajouter une note",
    },
  },
};

/**
 * Each tool group's title and one-line summary per language, keyed by the
 * group's id. Like the tools, English comes from the backend's catalogue, and
 * a group missing here falls back to it.
 */
const GROUP_TEXT: Record<Language, Record<string, ToolText>> = {
  nl: {
    account: { title: "Je account", summary: "Je aanmelding bij de connector" },
    clients: { title: "Klanten", summary: "Klanten, hun adressen en werven" },
    cranes: { title: "Kranen", summary: "De kranen en of ze vrij of verhuurd zijn" },
    orders: {
      title: "Orders en verhuringen",
      summary: "Orders, huurcontracten en waar ze lopen",
    },
    finance: {
      title: "Financieel",
      summary:
        "Facturen, betalingen, openstaande bedragen, omzet, niet-gefactureerd werk en transportprijzen",
    },
    system: {
      title: "Systeem",
      summary: "Odoo-velden en -codes, en het gebruik van de connector",
    },
    roadmap: {
      title: "Roadmap en ideeën",
      summary: "De roadmap van DC Data, en de ideeën en vragen voor Velta",
    },
    issues: {
      title: "Problemen van de connector",
      summary: "De mislukte oproepen van de connector, gebundeld in problemen, voor ontwikkelaars",
    },
  },
  en: {},
  fr: {
    account: { title: "Votre compte", summary: "Votre connexion au connecteur" },
    clients: { title: "Clients", summary: "Les clients, leurs adresses et leurs chantiers" },
    cranes: { title: "Grues", summary: "Les grues et si elles sont libres ou en location" },
    orders: {
      title: "Commandes et locations",
      summary: "Les commandes, les contrats de location et l'endroit où ils se déroulent",
    },
    finance: {
      title: "Finances",
      summary:
        "Les factures, les paiements, les montants ouverts, le chiffre d'affaires, les travaux non facturés et les prix de transport",
    },
    system: {
      title: "Système",
      summary: "Les champs et codes d'Odoo, et l'utilisation du connecteur",
    },
    roadmap: {
      title: "Feuille de route et idées",
      summary: "La feuille de route de DC Data, et les idées et questions transmises à Velta",
    },
    issues: {
      title: "Problèmes du connecteur",
      summary: "Les appels échoués du connecteur, regroupés en problèmes, pour les développeurs",
    },
  },
};

/** The text under `key` in `texts`, else `fallback`. */
function ownText(texts: Record<string, ToolText>, key: string, fallback: ToolText): ToolText {
  // Own entries only: a key such as "constructor" must not find Object's own members.
  const own = Object.hasOwn(texts, key) ? texts[key] : undefined;
  return own ?? { title: fallback.title, summary: fallback.summary };
}

/**
 * Whether DC Data serves or announces a tool, for any role: one with its
 * Dutch text here. The catalogue (`GET /mcp/overview`) lists only the tools
 * of the reader's own role, so a page that names someone else's call asks
 * this before it calls the tool no longer available.
 */
export function isKnownTool(name: string): boolean {
  return Object.hasOwn(TOOL_TEXT.nl, name);
}

/** Every tool `isKnownTool` knows: one with its Dutch text here. */
export function knownToolNames(): string[] {
  return Object.keys(TOOL_TEXT.nl);
}

/**
 * A tool's title in `language` from this file alone, for a tool the reader's
 * catalogue does not list; undefined in English, which comes from the
 * backend, and for a tool without one.
 */
export function ownToolTitle(name: string, language: Language): string | undefined {
  const texts = TOOL_TEXT[language];
  return Object.hasOwn(texts, name) ? texts[name]?.title : undefined;
}

/** A tool's title and summary in `language`, else the backend's own text. */
export function toolText(tool: { name: string } & ToolText, language: Language): ToolText {
  return ownText(TOOL_TEXT[language], tool.name, tool);
}

/** Tools with their title and summary in `language`; every other field as it came. */
export function withToolText<T extends { name: string } & ToolText>(
  tools: readonly T[],
  language: Language,
): T[] {
  return tools.map((tool) => ({ ...tool, ...toolText(tool, language) }));
}

/** A tool group's title and summary in `language`, else the backend's own text. */
export function groupText(group: { id: string } & ToolText, language: Language): ToolText {
  return ownText(GROUP_TEXT[language], group.id, group);
}

/** Tool groups with their title and summary in `language`; every other field as it came. */
export function withGroupText<T extends { id: string } & ToolText>(
  groups: readonly T[],
  language: Language,
): T[] {
  return groups.map((group) => ({ ...group, ...groupText(group, language) }));
}
